import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { portalUrls } from '@/config';
import { useAuth } from '@/lib/auth-context';
import { loginUrlWithReturn, authUrlWithReturn } from '@/lib/checkout';

function GuestLinks({ className = '' }) {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <a
        href={loginUrlWithReturn()}
        className="rounded-md px-3 py-1.5 text-xs sm:text-sm font-semibold text-white/90 transition hover:bg-black/20"
      >
        Sign in
      </a>
      <a
        href={authUrlWithReturn('signup')}
        className="rounded-md bg-black/25 px-3 py-1.5 text-xs sm:text-sm font-bold text-white transition hover:bg-black/40"
      >
        Sign up
      </a>
    </div>
  );
}

function ProfileMenu({ displayName, email, onLogout }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex max-w-[160px] items-center gap-1.5 rounded-md border border-white/20 bg-black/20 px-2.5 py-1.5 text-xs sm:text-sm font-semibold text-white transition hover:bg-black/35"
        title={email}
      >
        <span className="truncate">{displayName}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 text-white/70 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+0.5rem)] z-[60] min-w-[11rem] overflow-hidden rounded-lg border border-white/10 bg-[#0A0A0A] py-1 shadow-xl"
        >
          <a
            role="menuitem"
            href={portalUrls.dashboard}
            className="block px-4 py-2.5 text-sm font-medium text-white transition hover:bg-white/5"
            onClick={() => setOpen(false)}
          >
            Dashboard
          </a>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
            className="block w-full px-4 py-2.5 text-left text-sm text-gray-400 transition hover:bg-white/5 hover:text-white"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default function AuthNav({ className = '' }) {
  const { user, loading, isAuthenticated, displayName, logout } = useAuth();

  if (isAuthenticated && user) {
    return (
      <div className={className}>
        <ProfileMenu displayName={displayName} email={user.email} onLogout={logout} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <span className="h-8 w-20 animate-pulse rounded-md bg-black/25" aria-hidden />
      </div>
    );
  }

  return <GuestLinks className={className} />;
}
